import { useLayoutEffect, useRef } from 'react'
import { useFormikContext } from 'formik'

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the actual type of the value is not important
const getAllNestedPaths = (input: Record<string, any>, prefix = ''): string[] => {
  const isErrorType = (obj: Record<string, unknown>) =>
    Object.keys(obj).some((key) => ['message', 'ref', 'type'].includes(key))

  return Object.keys(input).reduce((paths: string[], key: string) => {
    const path = prefix ? `${prefix}.${key}` : key

    if (typeof input[key] === 'object' && !!input[key] && !isErrorType(input[key])) {
      return [...paths, ...getAllNestedPaths(input[key], path)]
    }

    return [...paths, path]
  }, [])
}

export const useScrollToFirstError = (): void => {
  const { errors, isSubmitting, submitCount } = useFormikContext()
  const lastScrolledSubmitCount = useRef(0)

  useLayoutEffect(() => {
    // Only react to a finished submit attempt, otherwise editing one field scrolls away to another
    if (isSubmitting || submitCount === lastScrolledSubmitCount.current || !Object.keys(errors).length) {
      return
    }

    lastScrolledSubmitCount.current = submitCount

    const [firstErrorElement] = getAllNestedPaths(errors).reduce<[HTMLElement | null, number]>(
      ([previousElement, previousTop], fieldName) => {
        const element = [...document.querySelectorAll('input'), ...document.querySelectorAll('textarea')].find(
          (el) => el.name === fieldName
        ) as HTMLInputElement

        const foundElement = element?.type === 'hidden' ? element.parentElement : element
        const top = foundElement?.getBoundingClientRect()?.top

        if (top !== undefined && (!previousTop || top < previousTop)) {
          return [foundElement, top]
        }

        return [previousElement, previousTop]
      },
      [null, 0]
    )

    firstErrorElement?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' })
  }, [errors, isSubmitting, submitCount])
}
